import { ActionState } from "@/types";
import { toast } from "sonner";

type Callbacks<T, R = unknown> = {
  onStart?: () => R;
  onEnd?: (reference: R) => void;
  onSuccess?: (result: T) => void;
  onError?: (result: T) => void;
};

export const withCallbacks = <Args extends unknown[], T extends ActionState, R = unknown>(
  fn: (...args: Args) => Promise<T>,
  callbacks: Callbacks<T, R>,
): ((...args: Args) => Promise<T>) => {
  return async (...args: Args) => {
    const promise = fn(...args);

    const reference = callbacks.onStart?.();

    const result = await promise;

    if (reference) {
      callbacks.onEnd?.(reference);
    }

    if (result?.status === "SUCCESS") {
      callbacks.onSuccess?.(result);
    }

    if (result?.status === "ERROR") {
      callbacks.onError?.(result);
    }

    return promise;
  };
};

type CreateToastCallbacksOptions = { loadingMessage?: string };

export const createToastCallbacks = (options?: CreateToastCallbacksOptions) => {
  return {
    onStart: () => {
      if (!options) return 0;
      return toast.loading(options.loadingMessage);
    },
    onEnd: (reference: string | number) => {
      toast.dismiss(reference);
    },
    onSuccess: (result: ActionState) => {
      if (result?.message) {
        toast.success(result.message);
      }
    },
    onError: (result: ActionState) => {
      if (result?.message) {
        toast.error(result.message);
      }
    },
  };
};

export const combineCallbacks = <T extends ActionState, R = unknown>(
  ...callbackSets: Partial<Callbacks<T, R>>[]
): Callbacks<T, R> => {
  const result: Partial<Callbacks<T, R>> = {};

  // For each callback type (onStart, onEnd, onSuccess, onError)
  ["onStart", "onEnd", "onSuccess", "onError"].forEach((key) => {
    const callbacksForType = callbackSets
      .map((cs) => cs[key as keyof Callbacks<T, R>])
      .filter(Boolean);

    if (callbacksForType.length > 0) {
      result[key as keyof Callbacks<T, R>] = ((...args: any[]) => {
        return callbacksForType.reduce((prevResult, callback) => {
          const currentResult = (callback as Function)(...args);
          return currentResult !== undefined ? currentResult : prevResult;
        }, undefined);
      }) as any;
    }
  });

  return result as Callbacks<T, R>;
};
